import { Search } from 'lucide-react';

import { Chip } from './primitives/Chip';
import { Section } from './primitives/Section';
import { MonoKicker } from './primitives/MonoKicker';

const problems = ['Hypothyroidism', 'Iron deficiency anaemia'];
const medications = ['Levothyroxine 75 mcg daily', 'Ferrous sulfate 325 mg BD'];

const filed = [
    {
        tone: 'sage',
        current: true,
        chip: 'Filed ✓',
        title: 'Follow-up — thyroid & energy',
        meta: 'Dictated 14 Aug 2026 · filed automatically · 42 min',
    },
    {
        tone: 'quiet',
        chip: 'Signed',
        current: false,
        meta: '2 Jun 2026 · 28 min',
        title: 'Follow-up — dose adjustment',
    },
    {
        chip: 'PDF',
        tone: 'quiet',
        current: false,
        title: 'CBC + thyroid panel',
        meta: 'Lab result · uploaded 28 May 2026',
    },
    {
        chip: '6 pp',
        tone: 'quiet',
        current: false,
        title: 'Records — Northside Endocrinology',
        meta: 'Outside record · faxed in 11 Mar 2026',
    },
    {
        tone: 'quiet',
        chip: 'Signed',
        current: false,
        title: 'New client intake',
        meta: '11 Mar 2026 · 55 min',
    },
] as const;

/**
 * The chart mock. Like `ScribeSection` this is bespoke on purpose.
 *
 * The 1 / 2 split uses grow ratios on a zero flex basis for the same reason
 * the hero does — see the note there. The search icon is lucide rather than
 * the mockup's Material Symbols: adding a Google icon font would breach the
 * no-external-fonts rule the whole app is built on.
 */
export function FilingSection() {
    return (
        <Section className="border-b border-border">
            <div className="flex flex-col gap-12">
                <div className="flex max-w-3xl flex-col gap-4">
                    <MonoKicker data-reveal>Build 02 · the filing</MonoKicker>
                    <h2
                        data-reveal
                        className="font-display text-[clamp(30px,4vw,40px)] leading-[1.15] text-balance"
                    >
                        Every scribe writes notes. Ours puts them away.
                    </h2>
                    <p
                        data-reveal
                        className="text-[clamp(16px,1.4vw,18px)] leading-[1.55] text-muted-foreground"
                    >
                        Notes file under the client, not the date — beside their
                        labs, their outside records and their history. Keep it
                        as your record, or let us push it straight into the EMR
                        you already run. Your filing rules; we configure them
                        either way.
                    </p>
                </div>

                <div
                    data-reveal
                    className="overflow-hidden rounded-[16px] border border-border bg-card"
                >
                    <div className="flex items-center gap-3 border-b border-border p-4 font-mono text-[13px] text-muted-foreground">
                        <Search
                            aria-hidden="true"
                            className="size-4 shrink-0"
                        />
                        Search every note, lab and record you&rsquo;ve ever
                        filed.
                    </div>

                    <div className="flex flex-wrap">
                        <div className="flex min-w-[min(260px,100%)] flex-[1_1_0%] flex-col gap-8 border-border bg-background p-[clamp(20px,3vw,32px)]">
                            <div className="flex flex-col gap-1">
                                <h3 className="font-display text-[26px] leading-[1.2]">
                                    Maya Ellison
                                </h3>
                                <MonoKicker>
                                    DOB 14 Mar 1988 · 36F · MRN 0142
                                </MonoKicker>
                            </div>

                            <div className="flex flex-col gap-3">
                                <MonoKicker>Problem list</MonoKicker>
                                <div className="flex flex-col items-start gap-2">
                                    {problems.map((problem) => (
                                        <span
                                            key={problem}
                                            className="rounded-[8px] border border-border bg-card px-3 py-1.5 font-mono text-[13px]"
                                        >
                                            {problem}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div className="flex flex-col gap-3">
                                <MonoKicker>Medications</MonoKicker>
                                <div className="flex flex-col gap-2 font-mono text-[13px]">
                                    {medications.map((med) => (
                                        <div key={med}>{med}</div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex flex-col gap-3">
                                <MonoKicker>Allergies</MonoKicker>
                                <Chip tone="oxblood" className="self-start">
                                    Penicillin — rash
                                </Chip>
                            </div>
                        </div>

                        <div className="flex min-w-[min(440px,100%)] flex-[2_1_0%] flex-col gap-6 border-l border-border p-[clamp(20px,3vw,32px)]">
                            <MonoKicker>Everything, under her name</MonoKicker>

                            <div className="flex flex-col gap-4">
                                {filed.map((item) => (
                                    <div
                                        key={item.title}
                                        className={`flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-border bg-background p-4 ${
                                            item.current ? '' : 'opacity-80'
                                        }`}
                                    >
                                        <div className="flex flex-col gap-1">
                                            <span className="font-semibold">
                                                {item.title}
                                            </span>
                                            <MonoKicker className="text-[11px]">
                                                {item.meta}
                                            </MonoKicker>
                                        </div>
                                        <Chip
                                            shape="pill"
                                            tone={
                                                item.tone === 'sage'
                                                    ? 'sage'
                                                    : 'quiet'
                                            }
                                        >
                                            {item.chip}
                                        </Chip>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Section>
    );
}
